import { type GenericShape } from '../../lib/shapes';
import { type Point } from '../../utils/math';
import { pointsToPath } from '../../utils/shapes';
import { type GenericPolyElement } from '../poly';
import { getPathFromPoints } from '../poly/utils/path';
import {
  getEllipseBezierHandles,
  getEllipsePoints,
  getPolyShape,
  getRectanglePoints,
} from '../poly/utils/shape';

import { type ColorCheckerElement, type ColorCheckerMode } from '.';

export const RECTANGLE_DONUT_INNER_SCALE = 1 / 3;
export const CIRCLE_DONUT_INNER_SCALE = 2 / 5;

const createDonutShape = (
  width: number,
  height: number,
  x: number,
  y: number,
  mode: 'rectangle' | 'circle',
  innerScale: number,
): string => {
  const isCircle = mode === 'circle';

  const outerPath = pointsToPath(
    isCircle
      ? getEllipsePoints(width, height)
      : getRectanglePoints(width, height),
    x,
    y,
    isCircle ? getEllipseBezierHandles(width, height) : undefined,
    { isClosed: true },
  );

  const innerPath = pointsToPath(
    isCircle
      ? getEllipsePoints(width, height, innerScale, true)
      : getRectanglePoints(width, height, innerScale, true),
    x,
    y,
    isCircle
      ? getEllipseBezierHandles(width, height, innerScale, true)
      : undefined,
    { isClosed: true },
  );

  const donutPath = outerPath.subtract(innerPath);
  const pathData = donutPath.pathData;

  outerPath.remove();
  innerPath.remove();
  donutPath.remove();

  return pathData;
};

export const calculateColorCheckerShape = (
  element: ColorCheckerElement,
): GenericShape => {
  if (element.mode === 'rectangle-donut') {
    return {
      data: createDonutShape(
        element.width,
        element.height,
        element.x,
        element.y,
        'rectangle',
        RECTANGLE_DONUT_INNER_SCALE,
      ),
      type: 'area',
    };
  }

  if (element.mode === 'circle-donut') {
    return {
      data: createDonutShape(
        element.width,
        element.height,
        element.x,
        element.y,
        'circle',
        CIRCLE_DONUT_INNER_SCALE,
      ),
      type: 'area',
    };
  }

  const points =
    element.mode === 'circle'
      ? getEllipsePoints(element.width, element.height)
      : getRectanglePoints(element.width, element.height);

  const bezierHandles =
    element.mode === 'circle'
      ? getEllipseBezierHandles(element.width, element.height)
      : undefined;

  const tempElement: GenericPolyElement = {
    ...element,
    points,
    bezierHandles,
  };

  return getPolyShape(tempElement);
};

export const getColorCheckerPoints = (
  mode: ColorCheckerMode,
  width: number,
  height: number,
): Point[] => {
  switch (mode) {
    case 'rectangle':
    case 'rectangle-donut':
      return getRectanglePoints(width, height);
    case 'circle':
    case 'circle-donut':
      return getEllipsePoints(width, height);
  }
};

export const getColorCheckerBezierHandles = (
  mode: ColorCheckerMode,
  width: number,
  height: number,
) => {
  switch (mode) {
    case 'circle':
    case 'circle-donut':
      return getEllipseBezierHandles(width, height);
    default:
      return undefined;
  }
};

export const initializeColorCheckerGeometry = (
  element: ColorCheckerElement,
): ColorCheckerElement => {
  element.points = getColorCheckerPoints(
    element.mode,
    element.width,
    element.height,
  );
  element.bezierHandles = getColorCheckerBezierHandles(
    element.mode,
    element.width,
    element.height,
  );
  element.shape = calculateColorCheckerShape(element);
  return element;
};

export const getColorCheckerPath2D = (element: ColorCheckerElement): Path2D => {
  if (element.mode === 'rectangle-donut') {
    return new Path2D(
      createDonutShape(
        element.width,
        element.height,
        0,
        0,
        'rectangle',
        RECTANGLE_DONUT_INNER_SCALE,
      ),
    );
  }

  if (element.mode === 'circle-donut') {
    return new Path2D(
      createDonutShape(
        element.width,
        element.height,
        0,
        0,
        'circle',
        CIRCLE_DONUT_INNER_SCALE,
      ),
    );
  }

  return getPathFromPoints(element.points, element.bezierHandles, true);
};
